import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ShoppingCart,
  TrendingUp,
  CreditCard,
  Wallet,
  ArrowUpRight,
  Shield,
  Clock,
  CheckCircle,
  Loader2,
  FileCheck,
  ExternalLink,
  ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = [
  {
    id: "stripe",
    name: "Stripe",
    icon: CreditCard,
    description: "Pay with credit/debit card",
    available: true,
    color: "bg-indigo-50 border-indigo-200 text-indigo-700",
    activeColor: "bg-indigo-100 border-indigo-500 ring-2 ring-indigo-300",
  },
  {
    id: "paypal",
    name: "PayPal",
    icon: Wallet,
    description: "Pay with PayPal balance",
    available: false,
    color: "bg-blue-50 border-blue-200 text-blue-700",
    activeColor: "bg-blue-100 border-blue-500 ring-2 ring-blue-300",
  },
  {
    id: "metamask",
    name: "MetaMask",
    icon: Shield,
    description: "Pay with crypto wallet",
    available: false,
    color: "bg-amber-50 border-amber-200 text-amber-700",
    activeColor: "bg-amber-100 border-amber-500 ring-2 ring-amber-300",
  },
];

function PriceProjectionChart({ projections }) {
  if (!projections) return null;
  const points = [
    { label: "Launch", price: projections.launch_price, month: 0 },
    { label: "6 Months", price: projections["6_months"], month: 6 },
    { label: "12 Months", price: projections["12_months"], month: 12 },
    { label: "24 Months", price: projections["24_months"], month: 24 },
  ];
  const maxPrice = Math.max(...points.map((p) => p.price));
  const minPrice = Math.min(...points.map((p) => p.price));
  const range = maxPrice - minPrice || 1;

  return (
    <div className="mt-4" data-testid="price-projection-chart">
      <div className="flex items-end justify-between gap-2 h-32 px-2">
        {points.map((point, i) => {
          const height = ((point.price - minPrice) / range) * 80 + 20;
          const growth = ((point.price - projections.launch_price) / projections.launch_price * 100).toFixed(1);
          return (
            <div key={i} className="flex flex-col items-center flex-1 gap-1">
              <span className="text-xs font-bold text-emerald-600">
                {i > 0 ? `+${growth}%` : "Base"}
              </span>
              <span className="text-xs font-semibold text-slate-900">
                ${point.price.toLocaleString()}
              </span>
              <div
                className="w-full rounded-t-md transition-all duration-500"
                style={{
                  height: `${height}%`,
                  background: i === 0
                    ? "#0F2744"
                    : `linear-gradient(to top, #0F2744, #3B82F6)`,
                  opacity: 0.7 + i * 0.1,
                }}
              />
              <span className="text-[10px] text-slate-500 mt-1">{point.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BuyTokensPage() {
  const [searchParams] = useSearchParams();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchTokens();
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2500;
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.info("Payment verification timed out. Check your transactions for status.");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/marketplace/checkout-status/${sessionId}`);
      if (data.payment_status === "paid") {
        setCheckingPayment(false);
        toast.success("Payment successful! Tokens have been added to your portfolio.");
        fetchTokens();
        return;
      } else if (data.status === "expired") {
        setCheckingPayment(false);
        toast.error("Payment session expired. Please try again.");
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch {
      setCheckingPayment(false);
      toast.error("Error verifying payment status.");
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  const fetchTokens = async () => {
    try {
      const { data } = await axios.get(`${API}/marketplace/tokens`);
      setTokens(data);
    } catch {
      toast.error("Failed to load marketplace data");
    } finally {
      setLoading(false);
    }
  };

  const handleBuyClick = (project) => {
    setSelectedProject(project);
    setQuantity(1);
    setPaymentMethod("stripe");
    setBuyDialogOpen(true);
  };

  const handlePurchase = async () => {
    if (!selectedProject) return;

    if (paymentMethod === "stripe") {
      setProcessing(true);
      try {
        const { data } = await axios.post(`${API}/marketplace/create-checkout`, {
          project_id: selectedProject.id,
          token_quantity: quantity,
          origin_url: window.location.origin,
        });
        if (data.url) {
          window.location.href = data.url;
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to create checkout session");
      } finally {
        setProcessing(false);
      }
    } else {
      toast.info(`${paymentMethod === "paypal" ? "PayPal" : "MetaMask"} integration coming soon. Please use Stripe for now.`);
    }
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

  const getScoreColor = (score) => {
    if (score >= 800) return "text-emerald-600";
    if (score >= 600) return "text-blue-600";
    if (score >= 400) return "text-amber-600";
    return "text-red-600";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="buy-tokens-loading">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="buy-tokens-page">
      {/* Checking payment overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" data-testid="payment-checking-overlay">
          <Card className="w-96 shadow-2xl">
            <CardContent className="p-8 text-center">
              <Loader2 className="w-12 h-12 text-[#3B82F6] animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">Verifying Payment</h3>
              <p className="text-sm text-slate-500 mt-2">Please wait while we confirm your transaction...</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Buy Tokens</h1>
        <p className="text-slate-500 mt-1">Browse security token offerings and invest in tokenized assets</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200" data-testid="stat-total-projects">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 bg-[#E8F4FD] rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-[#3B82F6]" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Available Offerings</p>
              <p className="text-2xl font-bold text-slate-900">{tokens.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="stat-avg-roi">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Average ROI</p>
              <p className="text-2xl font-bold text-emerald-600">
                {(tokens.reduce((s, t) => s + t.roi_percentage, 0) / tokens.length || 0).toFixed(1)}%
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="stat-base-price">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Token Price</p>
              <p className="text-2xl font-bold text-slate-900">$986</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Token Offerings */}
      <div className="space-y-6">
        {tokens.map((token) => (
          <Card
            key={token.id}
            className="border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
            data-testid={`token-card-${token.token_symbol}`}
          >
            <div className="flex flex-col lg:flex-row">
              {/* Left: Project Info */}
              <div className="lg:w-2/3 p-6">
                <div className="flex items-start gap-4 mb-4">
                  <img
                    src={token.image_url}
                    alt={token.name}
                    className="w-16 h-16 rounded-xl object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl font-bold text-slate-900">{token.name}</h3>
                      <span className="px-2 py-0.5 bg-[#0F2744] text-white text-xs font-mono rounded">
                        {token.token_symbol}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-sm text-slate-500">{token.industry}</span>
                      <span className="text-sm text-slate-400">|</span>
                      <span className="text-sm text-slate-500">{token.location}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                      <span className={`font-semibold ${getScoreColor(token.score)}`}>
                        <FileCheck className="w-3.5 h-3.5 inline mr-1" />
                        Score: {token.score}/1000
                      </span>
                      <span className="text-sm text-slate-500">
                        ROI: <span className="font-semibold text-emerald-600">{token.roi_percentage}%</span>
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-slate-600 line-clamp-2 mb-4">{token.description}</p>

                {/* Token Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Launch Price</p>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(token.token_price)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Total Supply</p>
                    <p className="text-lg font-bold text-slate-900">{token.token_supply.toLocaleString()}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Available</p>
                    <p className="text-lg font-bold text-emerald-600">{token.tokens_available.toLocaleString()}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Launch Date</p>
                    <p className="text-lg font-bold text-slate-900">
                      {token.launch_date ? new Date(token.launch_date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "TBA"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right: Price Projections + Buy */}
              <div className="lg:w-1/3 bg-slate-50 border-l border-slate-200 p-6 flex flex-col justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" /> Price Projections
                  </h4>
                  <PriceProjectionChart projections={token.price_projections} />
                  {token.price_projections?.growth_factors?.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {token.price_projections.growth_factors.map((f, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[11px] text-slate-600">
                          <ChevronRight className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Button
                  className="w-full mt-4 bg-[#0F2744] hover:bg-[#1E3A5F] text-white"
                  onClick={() => handleBuyClick(token)}
                  data-testid={`buy-btn-${token.token_symbol}`}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" /> Buy {token.token_symbol} Tokens
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Buy Dialog */}
      <Dialog open={buyDialogOpen} onOpenChange={setBuyDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="buy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-[#3B82F6]" />
              Purchase {selectedProject?.token_symbol} Tokens
            </DialogTitle>
          </DialogHeader>

          {selectedProject && (
            <div className="space-y-5">
              {/* Project Summary */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <img
                  src={selectedProject.image_url}
                  alt={selectedProject.name}
                  className="w-10 h-10 rounded-lg object-cover"
                />
                <div>
                  <p className="font-semibold text-slate-900">{selectedProject.name}</p>
                  <p className="text-xs text-slate-500">{selectedProject.industry} | {selectedProject.token_symbol}</p>
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Token Quantity
                </label>
                <div className="flex items-center gap-3">
                  <button
                    className="w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-700 font-bold transition-colors"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="qty-decrease"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(1000, parseInt(e.target.value) || 1)))}
                    className="flex-1 h-10 text-center border border-slate-200 rounded-lg font-semibold text-slate-900"
                    data-testid="qty-input"
                  />
                  <button
                    className="w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-700 font-bold transition-colors"
                    onClick={() => setQuantity(Math.min(1000, quantity + 1))}
                    data-testid="qty-increase"
                  >
                    +
                  </button>
                </div>
                <div className="flex justify-between mt-2">
                  {[1, 5, 10, 25, 50].map((q) => (
                    <button
                      key={q}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        quantity === q
                          ? "bg-[#0F2744] text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                      onClick={() => setQuantity(q)}
                      data-testid={`quick-qty-${q}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div className="p-4 bg-[#0F2744] rounded-lg text-white">
                <div className="flex justify-between text-sm">
                  <span className="text-white/70">Price per token</span>
                  <span>{formatCurrency(selectedProject.token_price)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-white/70">Quantity</span>
                  <span>{quantity}</span>
                </div>
                <div className="border-t border-white/20 my-2" />
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>{formatCurrency(selectedProject.token_price * quantity)}</span>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Payment Method
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {PAYMENT_METHODS.map((method) => (
                    <button
                      key={method.id}
                      onClick={() => method.available && setPaymentMethod(method.id)}
                      className={`relative p-3 rounded-lg border text-center transition-all ${
                        paymentMethod === method.id
                          ? method.activeColor
                          : method.available
                          ? `${method.color} hover:opacity-80 cursor-pointer`
                          : "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed opacity-60"
                      }`}
                      data-testid={`payment-method-${method.id}`}
                    >
                      <method.icon className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-xs font-semibold block">{method.name}</span>
                      {!method.available && (
                        <span className="absolute top-1 right-1 text-[8px] bg-slate-300 text-white px-1 rounded">
                          Soon
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBuyDialogOpen(false)}
              data-testid="cancel-buy-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={processing}
              className="bg-[#0F2744] hover:bg-[#1E3A5F] text-white"
              data-testid="confirm-buy-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" /> Pay {formatCurrency(selectedProject?.token_price * quantity || 0)}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
